import React, { useState, useEffect } from 'react';
import { Bell, BellOff, Loader2, CheckCircle, XCircle, Smartphone, AlertTriangle } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { toast } from 'sonner';
import {
  isPushSupported,
  getNotificationPermission,
  registerServiceWorker,
  requestNotificationPermission,
  subscribeToPush,
  unsubscribeFromPush,
  isSubscribed,
  sendTestNotification,
  isIOS,
  isStandalone
} from '../services/pushNotifications';

const PushNotificationManager = ({ token }) => {
  const [supported, setSupported] = useState(false);
  const [permission, setPermission] = useState('default');
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkStatus();
  }, []);

  const checkStatus = async () => {
    setLoading(true);
    const isSupported = isPushSupported();
    setSupported(isSupported);
    
    if (isSupported) {
      setPermission(getNotificationPermission());
      
      // Register SW on load
      await registerServiceWorker();
      
      // Check if already subscribed
      const sub = await isSubscribed();
      setSubscribed(sub);
    }
    setLoading(false);
  };

  const handleEnableNotifications = async () => {
    setLoading(true);
    
    try {
      // Vérifier si on a un token
      const authToken = token || localStorage.getItem('token');
      if (!authToken) {
        toast.error('Veuillez vous reconnecter');
        setLoading(false);
        return;
      }

      console.log('[Notif] Step 1: Checking environment...');
      console.log('[Notif] isIOS:', isIOS());
      console.log('[Notif] isStandalone:', isStandalone());
      console.log('[Notif] isPushSupported:', isPushSupported());
      
      if (isIOS() && !isStandalone()) {
        toast.error('Sur iPhone, vous devez installer l\'app sur l\'écran d\'accueil d\'abord !');
        setLoading(false);
        return;
      }

      // 1. Enregistrer le Service Worker d'abord
      console.log('[Notif] Step 2: Registering Service Worker...');
      const swRegistration = await registerServiceWorker();
      if (!swRegistration) {
        toast.error('Erreur Service Worker - Rechargez la page');
        setLoading(false);
        return;
      }
      console.log('[Notif] Service Worker OK');

      // 2. Request permission
      console.log('[Notif] Step 3: Requesting permission...');
      const permResult = await requestNotificationPermission();
      setPermission(getNotificationPermission());
      console.log('[Notif] Permission result:', permResult);
      
      if (!permResult.success) {
        if (permResult.permission === 'denied') {
          toast.error('Notifications bloquées. Allez dans Réglages → Notifications → Play MCB');
        } else {
          toast.error(permResult.error || 'Permission refusée');
        }
        setLoading(false);
        return;
      }
      
      // 3. Subscribe to push
      console.log('[Notif] Step 4: Subscribing to push...');
      const subResult = await subscribeToPush(authToken);
      console.log('[Notif] Subscribe result:', subResult);
      
      if (subResult.success) {
        setSubscribed(true);
        toast.success('🔔 Notifications activées ! Utilisez le bouton "Tester" pour vérifier.');
      } else {
        console.error('Subscribe error:', subResult.error);
        toast.error('Erreur: ' + (subResult.error || 'Échec de l\'activation'));
      }
    } catch (error) {
      console.error('Enable notifications error:', error);
      toast.error('Erreur: ' + error.message);
    }
    
    setLoading(false);
  };

  const handleDisableNotifications = async () => {
    setLoading(true);
    
    try {
      const result = await unsubscribeFromPush(token);
      if (result.success) {
        setSubscribed(false);
        toast.success('Notifications désactivées');
      } else {
        toast.error(result.error || 'Erreur lors de la désactivation');
      }
    } catch (error) {
      toast.error('Erreur: ' + error.message);
    }
    
    setLoading(false);
  };

  if (loading) {
    return (
      <Card className="border-0 shadow-sm">
        <CardContent className="p-4 flex items-center justify-center gap-2">
          <Loader2 className="h-5 w-5 animate-spin text-[#003366]" />
          <span className="text-gray-600">Vérification des notifications...</span>
        </CardContent>
      </Card>
    );
  }

  if (!supported) {
    // Detect iOS not in standalone mode
    const iosDevice = isIOS();
    const standaloneMode = isStandalone();
    
    return (
      <Card className="border-0 shadow-sm bg-amber-50">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Smartphone className="h-5 w-5 text-amber-600 mt-0.5" />
            <div>
              <p className="font-medium text-amber-800">Notifications non disponibles</p>
              {iosDevice && !standaloneMode ? (
                <div className="text-sm text-amber-700 mt-1 space-y-1">
                  <p><strong>Pour activer les notifications sur iPhone/iPad :</strong></p>
                  <ol className="list-decimal ml-4 space-y-1">
                    <li>Appuyez sur le bouton <strong>Partager</strong> (↑) en bas de Safari</li>
                    <li>Faites défiler et sélectionnez <strong>"Sur l'écran d'accueil"</strong></li>
                    <li>Appuyez sur <strong>"Ajouter"</strong></li>
                    <li><strong>Fermez Safari complètement</strong></li>
                    <li>Ouvrez l'app MCB depuis l'écran d'accueil</li>
                    <li>Reconnectez-vous et activez les notifications</li>
                  </ol>
                  <p className="mt-2 text-xs text-amber-600 bg-amber-100 p-2 rounded">
                    ⚠️ L'app DOIT être ouverte depuis l'icône sur l'écran d'accueil, pas depuis Safari
                  </p>
                </div>
              ) : iosDevice && standaloneMode ? (
                <div className="text-sm text-amber-700 mt-1 space-y-1">
                  <p>L'app est installée mais les notifications semblent bloquées.</p>
                  <p>Vérifiez dans <strong>Réglages iPhone → Notifications</strong> que l'app MCB est autorisée.</p>
                </div>
              ) : (
                <p className="text-sm text-amber-600">Installez l'application sur votre écran d'accueil pour recevoir les notifications.</p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (permission === 'denied') {
    return (
      <Card className="border-0 shadow-sm bg-red-50">
        <CardContent className="p-4 flex items-center gap-3">
          <BellOff className="h-5 w-5 text-red-600" />
          <div>
            <p className="font-medium text-red-800">Notifications bloquées</p>
            <p className="text-sm text-red-600">Réactivez-les dans les paramètres de votre navigateur.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-0 shadow-sm">
      <CardContent className="p-4">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            {subscribed ? (
              <div className="p-2 bg-emerald-100 rounded-full">
                <Bell className="h-5 w-5 text-emerald-600" />
              </div>
            ) : (
              <div className="p-2 bg-gray-100 rounded-full">
                <BellOff className="h-5 w-5 text-gray-500" />
              </div>
            )}
            <div>
              <p className="font-medium text-gray-800">
                {subscribed ? 'Notifications activées' : 'Notifications désactivées'}
              </p>
              <p className="text-sm text-gray-500">
                {subscribed 
                  ? 'Vous recevrez les alertes de nouvelles missions' 
                  : 'Activez pour être notifié des nouvelles missions'}
              </p>
            </div>
          </div>
          
          {subscribed ? (
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={async () => {
                  try {
                    const authToken = token || localStorage.getItem('token');
                    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/push/test`, {
                      method: 'POST',
                      headers: { 'Authorization': `Bearer ${authToken}` }
                    });
                    if (res.ok) {
                      toast.success('Notification de test envoyée !');
                    } else {
                      toast.error('Erreur envoi test');
                    }
                  } catch (e) {
                    toast.error('Erreur: ' + e.message);
                  }
                }}
                className="text-blue-600 border-blue-200 hover:bg-blue-50"
              >
                Tester
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleDisableNotifications}
                disabled={loading}
                className="text-red-600 border-red-200 hover:bg-red-50"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Désactiver'}
              </Button>
            </div>
          ) : (
            <Button
              size="sm"
              onClick={handleEnableNotifications}
              disabled={loading}
              className="bg-[#003366] hover:bg-[#002244]"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Bell className="h-4 w-4 mr-2" />}
              Activer
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default PushNotificationManager;
